import * as React from 'react';

import { Messages } from '../../../codegen/Localize';
import {
  ExplorableItemDefinitions,
} from '../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { ExplorerDropdown } from '../Components/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '../Components/ExplorerFlyoutPanel';
import { ExplorerListMenuPanelOwnProps } from '../Components/ExplorerListMenuPanel';
import { ShowWorkbookAsViewsValue } from './ConnectedFilterByTypeMenu';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';

export interface FilterByTypeDropdownProps {
  readonly filterSelected: (selectedContentTypes: ReadonlyArray<ExplorableItemType>) => void;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly menuPanelClass: React.ComponentClass<ExplorerListMenuPanelOwnProps>;
  readonly showWorkbooksAsViews?: boolean;
  readonly toggleShowWorkbooksAsViews?: (showWorkbooksAsViews: boolean) => void;
}

export class FilterByTypeDropdown extends React.PureComponent<FilterByTypeDropdownProps> {

  public render(): JSX.Element {
    return (
      <ExplorerDropdown
        buttonTitle={''}
        testId={ExplorerToolbarTestIds.FilterByTypeDropdown}
        renderButtonContent={this.renderButtonContent}
        renderPanel={this.renderPanel}
        panelTitle={Messages.filterByTypeDropdown_title() }
      />
    );
  }

  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return (
      <this.props.menuPanelClass
        onSelect={(value: string) => {
          this.handleSelection(value);
        }}
        anchorEl={props.anchorEl}
        testId={props.testId}
        onCloseRequest={props.onCloseRequest}
      />
    );
  }

  private renderButtonContent = (): JSX.Element => {
    return <span>{this.getButtonText()}</span>;
  }

  private getButtonText = (): string => {
    const selectedCount = this.props.contentFilter.length;
    const maxSelectedCount = this.props.defaultContentTypeFilterValues.length;

    if (selectedCount === maxSelectedCount) {
      return Messages.filterByTypeDropdown_showAll();
    } else if(selectedCount === 1) {
      if (this.props.contentFilter[0] === 'dataconnection') {
        return ExplorableItemDefinitions['datasource'].pluralName();
      } else {
        return ExplorableItemDefinitions[this.props.contentFilter[0]].pluralName();
      }
    } else if (selectedCount === 0) {
      return Messages.filterByTypeDropdown_showNone();
    } else {
      return Messages.filterByTypeDropdown_showSome();
    }
  }

  private handleSelection = (value: string) => {
    const selectedContentTypes: ExplorableItemType[] = [...this.props.contentFilter];

    if (value === ShowWorkbookAsViewsValue) {
      if (!this.props.toggleShowWorkbooksAsViews) {
        return;
      }
      const showWorkbooksAsViews = !this.props.showWorkbooksAsViews;
      this.props.toggleShowWorkbooksAsViews(showWorkbooksAsViews);
      swapWorkbookAndViewContentTypes(selectedContentTypes, showWorkbooksAsViews);
    } else if (selectedContentTypes.length === 1 && selectedContentTypes[0] === 'dataconnection' && value === 'datasource') {
      // Special case when data connection is being shown but user goes to unselect data source
      // Remove data connection from selected content types.
      selectedContentTypes.splice(0, 1);
    } else {
      toggleSelectedContentType(selectedContentTypes, value as ExploreContentApi_ContentType);
    }

    // if selected content type contains dataconnection swap it with datasource
    const indexOfDataconnection = selectedContentTypes.indexOf('dataconnection');
    if (indexOfDataconnection > -1) {
      selectedContentTypes[indexOfDataconnection] = 'datasource';
    }

    this.props.filterSelected(selectedContentTypes);
  }
}

/**
 * Modifies the ContentType array provided, swapping workbook for view, if showWorkbooksAsViews is true;
 * Otherwise, it swaps view for workbook. If neither is present in the array, it is not modified at all.
 */
function swapWorkbookAndViewContentTypes(selectedContentTypes: ExplorableItemType[], showWorkbooksAsViews: boolean) {
  const swapFrom = showWorkbooksAsViews ? 'workbook' : 'view';
  const swapWith = showWorkbooksAsViews ? 'view' : 'workbook';
  const indexOfSwap = selectedContentTypes.indexOf(swapFrom);
  if (indexOfSwap > -1) {
    selectedContentTypes[indexOfSwap] = swapWith;
  }
}

/**
 * Modifies the ContentType array provided, adding or removing the specified contentTypeToToggle.
 */
function toggleSelectedContentType(selectedContentTypes: ExplorableItemType[], contentTypeToToggle: ExploreContentApi_ContentType) {
    const selectedIndex = selectedContentTypes.indexOf(contentTypeToToggle);

    if (selectedIndex > -1) {
      selectedContentTypes.splice(selectedIndex, 1);
    } else {
      selectedContentTypes.push(contentTypeToToggle);
    }
}
